import { useTranslation } from 'react-i18next';
import { Badge, Text, cn } from '@kognitos/lattice';

/**
 * How a table value presents itself, independent of the column it came from.
 */
export type TOutputTableCellVariant = 'badge' | 'mono' | 'text';

const IDENTIFIER_WORDS = new Set(['id', 'ids', 'invoice', 'invoices']);

/**
 * The presentation a column's header implies, for callers that don't decide
 * it themselves.
 *
 * Header words are matched whole, so `Paid`, `Video`, `Grid` and `Residual`
 * stay proportional despite containing `id`.
 *
 * @param header - The column's header, raw or humanised
 * @returns The variant that column's values should render with
 */
export function getOutputTableCellVariant(
  header: string,
): TOutputTableCellVariant {
  if (/^status$/i.test(header)) {
    return 'badge';
  }
  const words = header
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .split(/[^a-zA-Z0-9]+/)
    .map(word => word.toLowerCase());
  return words.some(word => IDENTIFIER_WORDS.has(word)) ? 'mono' : 'text';
}

/**
 * Shared value treatment for output tables, regardless of presentation size: a
 * status reads as a badge, an identifier as monospace, everything else as
 * single-line text that ellipsizes.
 *
 * The table owns the choice and passes `variant`; without one the header
 * decides, via {@link getOutputTableCellVariant}.
 *
 * Alignment is deliberately not decided here. The cell around this one owns
 * it, because only the table knows whether the whole column is numeric.
 */
export function OutputTableCell({
  header,
  value,
  variant,
  compact = false,
}: {
  header: string;
  value: string;
  variant?: TOutputTableCellVariant;
  compact?: boolean;
}) {
  const { t } = useTranslation('outputs');
  const displayValue =
    value || t('outputs.table.emptyCell', { defaultValue: '\u2014' });
  const presentation = variant ?? getOutputTableCellVariant(header);

  if (presentation === 'badge') {
    const tone = /matched|paid|cleared|complete|success/i.test(value)
      ? 'success'
      : /exception|review|pending|warning/i.test(value)
        ? 'warning'
        : 'secondary';
    return (
      <Badge
        variant={tone}
        className={compact ? 'max-w-full truncate px-1 py-0' : undefined}
      >
        {displayValue}
      </Badge>
    );
  }
  return (
    <Text
      level={compact ? 'xSmall' : 'small'}
      className={cn(
        'block w-full truncate tabular-nums',
        presentation === 'mono' && 'font-mono',
      )}
    >
      {displayValue}
    </Text>
  );
}
